import { createAsyncThunk } from '@reduxjs/toolkit';
import { UploadsTableFields } from '../../../components/productInfo/uploads/Uploads.types';
import { createMultipartData } from '../../../helpers/multipartData';
import { http, httpMultipart } from '../../../helpers/request';
import { ActionResponse, CommonResponse } from '../../../models/actionResponse/types';
import { Comment } from '../../../models/comment/types';
import {
    AddLiveGuidelineOwnerRequest,
    AddLiveGuidelineProductCommentRequest,
    CreateLiveGuidelineRequest,
    DeleteLiveGuidelineProductCommentRequest,
    DeleteLiveGuidelineProductFileRequest,
    GetLiveGuidelineNotificationsRequest,
    GetLiveGuidelineProductFilesRequest,
    GetLiveGuidelinesQuery,
    HideLiveGuidelineProductRequest,
    LiveGuideline,
    LiveGuidelineDetail,
    LiveGuidelineNotificationInformation,
    LiveGuidelineNotificationPreference,
    LiveGuidelineNotificationPreferenceResponse,
    LiveGuidelineNotificationPreferencesResponse,
    LiveGuidelineOwner,
    LiveGuidelineProduct,
    LiveGuidelineProductRef,
    LiveGuidelineRecentlyModified,
    LiveGuidelineRecentUpdates,
    RemoveLiveGuidelineOwnerRequest,
    SaveLiveGuidelineCriteriaRequest,
    UpdateLiveGuidelineProductFileRequest,
    UpdateLiveGuidelineRequest,
    UpdateLiveGuidelineNotificationPreferenceRequest,
    UploadLiveGuidelineProductFileRequest,
} from './types';

const baseUrl = '/live-guidelines';

export const createLiveGuidelineDraftAction = createAsyncThunk<
    ActionResponse<LiveGuideline> | undefined,
    CreateLiveGuidelineRequest
>('liveGuidelines/createDraft', async (data: CreateLiveGuidelineRequest) => {
    const result = await http<ActionResponse<LiveGuideline>, CreateLiveGuidelineRequest>({
        path: `${baseUrl}`,
        method: 'post',
        body: data,
    });
    return result.parsedBody;
});

export const saveLiveGuidelineCriteriaAction = createAsyncThunk<
    ActionResponse<LiveGuideline> | undefined,
    SaveLiveGuidelineCriteriaRequest
>('liveGuidelines/saveCriteria', async (data: SaveLiveGuidelineCriteriaRequest) => {
    const result = await http<ActionResponse<LiveGuideline>, SaveLiveGuidelineCriteriaRequest>({
        path: `${baseUrl}/${data.liveGuidelineId}/criteria`,
        method: 'post',
        body: data,
    });
    return result.parsedBody;
});

export const getLiveGuidelinesListAction = createAsyncThunk<
    ActionResponse<Array<LiveGuideline>> | undefined,
    GetLiveGuidelinesQuery
>('liveGuidelines/getList', async (data: GetLiveGuidelinesQuery) => {
    const result = await http<ActionResponse<Array<LiveGuideline>>, GetLiveGuidelinesQuery>({
        path: `${baseUrl}/list`,
        method: 'post',
        body: data,
    });
    return result.parsedBody;
});

export const getLiveGuidelineByIdAction = createAsyncThunk<
    ActionResponse<LiveGuidelineDetail> | undefined,
    number
>('liveGuidelines/getById', async (id: number) => {
    const result = await http<ActionResponse<LiveGuidelineDetail>, undefined>({
        path: `${baseUrl}/${id}`,
        method: 'get',
    });
    return result.parsedBody;
});

export const getLiveGuidelinePotentialOwnersAction = createAsyncThunk<
    ActionResponse<Array<LiveGuidelineOwner>> | undefined,
    number
>('liveGuidelines/getPotentialOwners', async (id: number) => {
    const result = await http<ActionResponse<Array<LiveGuidelineOwner>>, undefined>({
        path: `${baseUrl}/${id}/potential-owners`,
        method: 'get',
    });
    return result.parsedBody;
});

export const updateLiveGuidelineAction = createAsyncThunk<
    ActionResponse<LiveGuideline> | undefined,
    UpdateLiveGuidelineRequest
>('liveGuidelines/updateProperties', async (data: UpdateLiveGuidelineRequest) => {
    const result = await http<ActionResponse<LiveGuideline>, UpdateLiveGuidelineRequest>({
        path: `${baseUrl}/${data.id}`,
        method: 'put',
        body: data,
    });
    return result.parsedBody;
});

export const deleteLiveGuidelineAction = createAsyncThunk<void, number>(
    'liveGuidelines/delete',
    async (id: number) => {
        await http<void, number>({
            path: `${baseUrl}/${id}`,
            method: 'delete',
        });
    },
);

export const addLiveGuidelineOwnerAction = createAsyncThunk<void, AddLiveGuidelineOwnerRequest>(
    'liveGuidelines/addOwner',
    async (data: AddLiveGuidelineOwnerRequest) => {
        await http<void, AddLiveGuidelineOwnerRequest>({
            path: `${baseUrl}/${data.liveGuidelineId}/owners`,
            method: 'post',
            body: data,
        });
    },
);

export const removeLiveGuidelineOwnerAction = createAsyncThunk<
    void,
    RemoveLiveGuidelineOwnerRequest
>('liveGuidelines/removeOwner', async (data: RemoveLiveGuidelineOwnerRequest) => {
    await http<void, undefined>({
        path: `${baseUrl}/${data.liveGuidelineId}/owners/${data.userId}`,
        method: 'delete',
    });
});

export const getLiveGuidelineProductsAction = createAsyncThunk<
    ActionResponse<Array<LiveGuidelineProduct>> | undefined,
    number
>('liveGuidelines/getProducts', async (id: number) => {
    const result = await http<ActionResponse<Array<LiveGuidelineProduct>>, undefined>({
        path: `${baseUrl}/${id}/products`,
        method: 'get',
    });
    return result.parsedBody;
});

export const hideLiveGuidelineProductAction = createAsyncThunk<
    void,
    HideLiveGuidelineProductRequest
>('liveGuidelines/hideProduct', async (data: HideLiveGuidelineProductRequest) => {
    await http<void, undefined>({
        path: `${baseUrl}/${data.liveGuidelineId}/products/${data.productId}/hide`,
        method: 'post',
    });
});

export const restoreLiveGuidelineProductAction = createAsyncThunk<
    void,
    HideLiveGuidelineProductRequest
>('liveGuidelines/restoreProduct', async (data: HideLiveGuidelineProductRequest) => {
    await http<void, undefined>({
        path: `${baseUrl}/${data.liveGuidelineId}/products/${data.productId}/restore`,
        method: 'post',
    });
});

const productUrl = (data: LiveGuidelineProductRef) =>
    `${baseUrl}/${data.liveGuidelineId}/products/${data.liveGuidelineProductId}`;

export const getLiveGuidelineProductCommentsAction = createAsyncThunk<
    ActionResponse<Array<Comment>> | undefined,
    LiveGuidelineProductRef
>('liveGuidelines/getProductComments', async (data: LiveGuidelineProductRef) => {
    const result = await http<ActionResponse<Array<Comment>>, undefined>(
        { path: `${productUrl(data)}/comments`, method: 'get' },
        true,
        false,
    );
    return result.parsedBody;
});

export const addLiveGuidelineProductCommentAction = createAsyncThunk<
    ActionResponse<Comment> | undefined,
    AddLiveGuidelineProductCommentRequest
>('liveGuidelines/addProductComment', async (data: AddLiveGuidelineProductCommentRequest) => {
    const result = await http<ActionResponse<Comment>, { comment: string }>(
        { path: `${productUrl(data)}/comments`, method: 'post', body: { comment: data.comment } },
        true,
        false,
    );
    return result.parsedBody;
});

export const deleteLiveGuidelineProductCommentAction = createAsyncThunk<
    CommonResponse | undefined,
    DeleteLiveGuidelineProductCommentRequest
>('liveGuidelines/deleteProductComment', async (data: DeleteLiveGuidelineProductCommentRequest) => {
    const result = await http<CommonResponse, undefined>(
        { path: `${productUrl(data)}/comments/${data.commentId}`, method: 'delete' },
        true,
        false,
    );
    return result.parsedBody;
});

export const deleteAllLiveGuidelineProductCommentsAction = createAsyncThunk<
    CommonResponse | undefined,
    LiveGuidelineProductRef
>('liveGuidelines/deleteAllProductComments', async (data: LiveGuidelineProductRef) => {
    const result = await http<CommonResponse, undefined>(
        { path: `${productUrl(data)}/comments`, method: 'delete' },
        true,
        false,
    );
    return result.parsedBody;
});

export const getLiveGuidelineProductFilesAction = createAsyncThunk<
    ActionResponse<Array<UploadsTableFields>> | undefined,
    GetLiveGuidelineProductFilesRequest
>('liveGuidelines/getProductFiles', async (data: GetLiveGuidelineProductFilesRequest) => {
    const result = await http<
        ActionResponse<Array<UploadsTableFields>>,
        GetLiveGuidelineProductFilesRequest
    >({ path: `${productUrl(data)}/uploads/list`, method: 'post', body: data }, true, false);
    return result.parsedBody;
});

export const uploadLiveGuidelineProductFileAction = createAsyncThunk<
    any | undefined,
    UploadLiveGuidelineProductFileRequest
>('liveGuidelines/uploadProductFile', async (data: UploadLiveGuidelineProductFileRequest) => {
    const { liveGuidelineId, liveGuidelineProductId, ...fileData } = data;
    const result = await httpMultipart({
        path: `${productUrl({ liveGuidelineId, liveGuidelineProductId })}/uploads`,
        method: 'post',
        body: createMultipartData(fileData),
    });
    return result?.data;
});

export const updateLiveGuidelineProductFileAction = createAsyncThunk<
    ActionResponse<UploadsTableFields> | undefined,
    UpdateLiveGuidelineProductFileRequest
>('liveGuidelines/updateProductFile', async (data: UpdateLiveGuidelineProductFileRequest) => {
    const result = await http<
        ActionResponse<UploadsTableFields>,
        UpdateLiveGuidelineProductFileRequest
    >(
        { path: `${productUrl(data)}/uploads/${data.projectProductUploadId}`, method: 'put', body: data },
        true,
        false,
    );
    return result.parsedBody;
});

export const deleteLiveGuidelineProductFileAction = createAsyncThunk<
    CommonResponse | undefined,
    DeleteLiveGuidelineProductFileRequest
>('liveGuidelines/deleteProductFile', async (data: DeleteLiveGuidelineProductFileRequest) => {
    const result = await http<CommonResponse, undefined>(
        { path: `${productUrl(data)}/uploads/${data.projectProductUploadId}`, method: 'delete' },
        true,
        false,
    );
    return result.parsedBody;
});

export const getLiveGuidelineRecentUpdatesAction = createAsyncThunk<
    ActionResponse<LiveGuidelineRecentUpdates> | undefined,
    number
>('liveGuidelines/getRecentUpdates', async (id: number) => {
    const result = await http<ActionResponse<LiveGuidelineRecentUpdates>, undefined>({
        path: `${baseUrl}/${id}/recent-updates`,
        method: 'get',
    });
    return result.parsedBody;
});

export const getLiveGuidelineRecentlyModifiedAction = createAsyncThunk<
    ActionResponse<LiveGuidelineRecentlyModified[]> | undefined,
    boolean
>('liveGuidelines/getRecentlyModified', async (onlySubscribed: boolean) => {
    const result = await http<ActionResponse<LiveGuidelineRecentlyModified[]>, undefined>({
        path: `${baseUrl}/recent-updates?onlySubscribed=${onlySubscribed}`,
        method: 'get',
    });
    return result.parsedBody;
});

export const getLiveGuidelineNotificationsAction = createAsyncThunk<
    ActionResponse<LiveGuidelineNotificationInformation> | undefined,
    GetLiveGuidelineNotificationsRequest
>('liveGuidelines/getNotifications', async (data: GetLiveGuidelineNotificationsRequest) => {
    const result = await http<
        ActionResponse<LiveGuidelineNotificationInformation>,
        GetLiveGuidelineNotificationsRequest
    >({
        path: `${baseUrl}/${data.liveGuidelineId}/notifications`,
        method: 'post',
        body: data,
    });
    return result.parsedBody;
});

export const getLiveGuidelineNotificationPreferencesAction = createAsyncThunk<
    LiveGuidelineNotificationPreferencesResponse | undefined,
    void
>('liveGuidelines/getNotificationPreferences', async () => {
    const result = await http<LiveGuidelineNotificationPreferencesResponse, undefined>({
        path: `${baseUrl}/notification-preferences`,
        method: 'get',
    });
    return result.parsedBody;
});

export const updateLiveGuidelineNotificationPreferenceAction = createAsyncThunk<
    LiveGuidelineNotificationPreferenceResponse | undefined,
    { liveGuidelineId: number; preference: UpdateLiveGuidelineNotificationPreferenceRequest }
>('liveGuidelines/updateNotificationPreference', async ({ liveGuidelineId, preference }) => {
    const result = await http<
        LiveGuidelineNotificationPreferenceResponse,
        UpdateLiveGuidelineNotificationPreferenceRequest
    >({
        path: `${baseUrl}/${liveGuidelineId}/notification-preferences`,
        method: 'put',
        body: preference,
    });
    return result.parsedBody;
});

export const syncLiveGuidelineAction = createAsyncThunk<any | undefined, number>(
    'liveGuidelines/sync',
    async (id: number) => {
        await http<void, undefined>({
            path: `${baseUrl}/${id}/sync`,
            method: 'post',
        });
    },
);
